import React from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { object } from "yup";

import { UserInfoFormikValues } from "../types";
import { PrimaryButton } from "./primary-button";
import { PhoneNumberInput } from "./phone-number-input";

import { baseUserInfoValidationSchema, emailValidationAtom } from "../shared";

const validationSchema = object().shape({
  ...baseUserInfoValidationSchema,
  ...emailValidationAtom,
});

interface UserInfoFormikProps {
  onSubmit: (values: UserInfoFormikValues) => Promise<void>;
  name: string;
  privacyUrl: string;
  phone?: string;
  tosUrl: string;
}

export function UserInfoFormikWithEmail(props: UserInfoFormikProps) {
  const phoneNumberInitialValues: UserInfoFormikValues = {
    email: "",
    tosConsent: false,
    phone: props.phone,
  };

  let possessiveName = props.name
    ? props.name.endsWith("s")
      ? `${props.name}'`
      : `${props.name}'s`
    : "the";

  return (
    <Formik
      initialValues={phoneNumberInitialValues}
      onSubmit={props.onSubmit}
      validateOnChange={false}
      validateOnBlur={false}
      validationSchema={validationSchema}
    >
      {({ isSubmitting }) => (
        <Form>
          <div className="space-y-4">
            <div data-field-group className="space-y-2">
              <div data-field className="flex-1 relative">
                <label className="sr-only" htmlFor="email">
                  Email Address
                </label>
                <div className="relative h-full">
                  <Field
                    id="email"
                    placeholder="Enter email address"
                    type="email"
                    name="email"
                    className="w-full h-full border-radius-primary border-[#C9CCCF] focus-primary focus-primary-border placeholder-subdued text-body"
                  />
                  <ErrorMessage
                    component="p"
                    className="caption text-critical mt-2"
                    name="email"
                  />
                </div>
              </div>
              <div data-field className="flex-1 relative">
                <label className="sr-only" htmlFor="phone">
                  Phone Number
                </label>
                <div className="relative h-full">
                  <PhoneNumberInput name="phone" />
                  <ErrorMessage
                    component="p"
                    className="caption text-critical mt-2"
                    name="phone"
                  />
                </div>
              </div>
            </div>
            <div data-field className="space-y-2">
              <div className="space-y-2">
                <div data-field className="space-y-2">
                  <div className="flex">
                    <div className="flex items-center h-5">
                      <Field
                        id="tosConsent"
                        name="tosConsent"
                        type="checkbox"
                        className="focus-primary text-primary h-4 w-4 border-gray-300 border-radius-sm"
                      />
                    </div>
                    <div className="ml-2 text-sm">
                      <label
                        htmlFor="tosConsent"
                        className="caption select-none text-subdued"
                      >
                        <span>
                          I agree to receive automated text messages at the
                          phone number provided. For more information, see{" "}
                          {possessiveName}{" "}
                          <a className="underline" href={props.tosUrl}>
                            Terms of Service
                          </a>{" "}
                          and{" "}
                          <a className="underline" href={props.privacyUrl}>
                            Privacy Policy
                          </a>
                          .
                        </span>
                      </label>
                    </div>
                  </div>
                  <ErrorMessage
                    component="p"
                    className="caption text-critical"
                    name="tosConsent"
                  />
                </div>
              </div>
            </div>
            <div>
              <PrimaryButton
                className="w-full justify-center"
                type="submit"
                loading={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Next Step"}
              </PrimaryButton>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
}
